import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Pagination,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import colors from '../theme/Colors';
import CustomSelect from '../components/CustomSelect';
import SettingsIcon from '@mui/icons-material/Settings';
import Navbar from '../components/Navbar';
import RateBox from '../components/RateBox';
import UpdateRateForm from '../components/UpdateRateForm';
import { api } from '../helpers/apiRequest';
import { toast } from 'react-toastify';
import ManagementDialog from '../components/ManagementDialog';
import { pageAccessList } from '../helpers/pageAccessList';

const Rates = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Less than 600px
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md')); // Less than 900px

  const [rates, setRates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parties, setParties] = useState([]);
  const [pStyles, setPStyles] = useState([]);
  const [styles, setStyles] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedRate, setSelectedRate] = useState({});
  const [filters, setFilters] = useState({
    customer: 'all',
    party: 'all',
    p_style: 'all',
    style: 'all',
    item: 'all'
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [managementType, setManagementType] = useState('party'); 
  const [isEditing, setIsEditing] = useState(false);
  const [newRate, setNewRate] = useState({
    customer: '',
    party: '',
    p_style: '',
    style: '',
    item: '',
    price: '',
    updated_by: '',
  });
  const handleOpenManagementDialog = (type) => {
    setManagementType(type);
    setManagementDialogOpen(true);
  };

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);

  const [addRecordDialog, setAddRecordDialog] = useState({
    open: false,
    type: '',
    name: '',
    party: '',
    customer: ''
  });

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    rateId: null,
  });

  const [partySpecificOptions, setPartySpecificOptions] = useState({
    pStyles: [],
    styles: [],
    items: []
  });
  const [userInfo] = useState(JSON.parse(localStorage.getItem('user')));
  const [accessControl, setAccessControl] = useState({});
  const fetchCurrentUser = async () => {
    try {
      let user = await localStorage.getItem('user');
      user = JSON.parse(user);

      if (user) {
        const res = await api.get(`/users/get/routes?userId=${userInfo?.id}`);
        const accessArray = res.data.message || [];
        // Map route permissions by route key
        const accessMapFromServer = accessArray.reduce((map, item) => {
          map[item.route] = item.allowed;
          return map;
        }, {});

        // Merge with full pageAccessList to ensure all routes are covered
        const accessMap = pageAccessList.reduce((acc, page) => {
          acc[page.key] = accessMapFromServer[page.key] || false;
          return acc;
        }, {});
        console.log("setAccessControl", accessControl);
        setAccessControl(accessMap);
      }
    } catch (error) {
      console.log(error);
    }
  }
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  const handleUpdateDialogOpen = (rate) => {
    setIsEditing(true);
    setNewRate({
      customer: rate?.customer?._id,
      party: rate?.party?._id,
      p_style: rate?.p_style?._id,
      style: rate?.style?._id,
      item: rate?.item?._id,
      price: rate?.price,
      _id: rate?._id
    });
    setOpenDialog(true);
    fetchPartySpecificOptions(rate?.party?._id);
  };

  const handleOpenDialog = () => {
    setIsEditing(false);
    setNewRate({
      customer: '',
      party: '',
      p_style: '',
      style: '',
      item: '',
      price: '',
      updated_by: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewRate((prev) => ({ ...prev, customer: filters.customer }));
    setIsEditing(false);
  };

  const handleAddRate = async () => {
    try {
      const requiredFields = {
        customer: 'Customer',
        party: 'Party',
        p_style: 'P-Style',
        // style: 'Style',
        item: 'Item',
        price: 'Price'
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([key]) => !newRate[key])
        .map(([_, label]) => label);

      if (missingFields.length > 0) {
        toast.error(`Please select ${missingFields.join(', ')}`);
        return;
      }

      let user = await localStorage.getItem('user');
      user = JSON.parse(user);

      const updatedRate = {
        ...newRate,
        updated_by: user.id
      };

      const response = isEditing 
        ? await api.put(`inventory/rate_update/${newRate._id}`, updatedRate)
        : await api.post(`inventory/rate`, updatedRate);

      if (response.status) {
        toast.success(`Rate ${isEditing ? 'updated' : 'added'} successfully`);
        fetchRates(pprRef.current,filtersRef.current);
        setNewRate(prev => ({
          customer: '',
          party: '',
          p_style: '',
          style: '',
          item: '',
          price: '',
          updated_by: '',
        }));
        handleCloseDialog();
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'add'} rate`);
      }
    } catch (error) {
      toast.error(`Error ${isEditing ? 'updating' : 'adding'} rate`);
      console.error(`Error ${isEditing ? 'updating' : 'adding'} rate:`, error.response?.data || error.message);
    }
  };
  const handleFilterChange = async (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  
    if (key === 'party' && value !== 'all') {
      try {
        const [pStylesRes, stylesRes, itemsRes] = await Promise.all([
          api.get(`inventory/pstyles_party?party=${value}`),
          api.get(`inventory/styles_party?party=${value}`),
          api.get(`inventory/items_party?type=r&party=${value}`)
        ]);
  
        // Update the dropdown options with party-specific data
        setPStyles(pStylesRes.data.message || []);
        setStyles(stylesRes.data.message || []);
        setItems(itemsRes.data.message || []);
  
        // Reset dependent filters
        setFilters(prev => ({
          ...prev,
          p_style: 'all',
          style: 'all',
          item: 'all'
        }));
      } catch (error) {
        console.error('Error fetching party-specific options:', error);
        toast.error('Error fetching options for selected party');
      }
    } else if (key === 'party' && value === 'all') {
      setPStyles([]);
      setStyles([]);
      setItems([]);
  
      setFilters(prev => ({
        ...prev,
        p_style: 'all',
        style: 'all',
        item: 'all'
      }));
    } 
    
    // 🔹 Fetch items when p_style changes
    else if (key === 'p_style' && value !== 'all') {
      try {
        const itemsRes = await api.get(`inventory/items_party?type=r&party=${filters?.party}&pstyle=${value}`);
        setItems(itemsRes.data.message || []);
  
        // Reset dependent filters
        setFilters(prev => ({
          ...prev,
          style: 'all',
          item: 'all'
        }));
      } catch (error) {
        console.error('Error fetching items for p_style:', error);
        toast.error('Error fetching items for selected p_style');
      }
    } else if (key === 'p_style' && value === 'all') {
      setItems([]);
      setFilters(prev => ({
        ...prev,
        style: 'all',
        item: 'all'
      }));
    }
  };
  
  const handleOpenHistoryDialog = async (rate) => {
    setHistoryDialog(true);
    setLoading(true);
    try {
      const data = await api.get(`inventory/rate/${rate._id}`);

      setSelectedRate(data.data.message);
    } catch (error) {
      console.error("Error fetching rate details:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleCloseHistoryDialog = () => {
    setHistoryDialog(false);
    setSelectedRate(null);
  };

  const fetchDropdownData = async () => {
    try {
      const [customersRes, partiesRes] = await Promise.all([
        api.get(`users/users/role/customer`),
        api.get(`inventory/party`),
      ]);

      const customersList = customersRes.data.data;

      setCustomers(customersList);
      setParties(partiesRes.data.message);

      setPStyles([]);
      setStyles([]);
      setItems([]);


    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };


  const fetchRates = async (pprRef,filtersRef) => {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append('page', page);
      queryParams.append('limit', pprRef);

      if (filtersRef.customer !== 'all') queryParams.append('customer', filtersRef.customer);
      if (filtersRef.party !== 'all') queryParams.append('party', filtersRef.party);
      if (filtersRef.p_style !== 'all') queryParams.append('p_style', filtersRef.p_style);
      if (filtersRef.style !== 'all') queryParams.append('style', filtersRef.style);
      if (filtersRef.item !== 'all') queryParams.append('item', filtersRef.item);
     
      const response = await api.get(`inventory/searchrates?${queryParams.toString()}`);
      setRates(response?.data?.data);
      setTotalPages(Math.ceil(response?.data?.total_count / pprRef));
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const fetchPartySpecificOptions = async (partyId, pStyleId = null) => {
    try {
      const [pStylesRes, stylesRes, itemsRes] = await Promise.all([
        api.get(`inventory/pstyles_party?party=${partyId}`),
        api.get(`inventory/styles_party?party=${partyId}`),
        api.get(
          `inventory/items_party?party=${partyId}${
            isEditing ? '&type=r' : ''
          }${pStyleId ? `&pstyle=${pStyleId}` : ''}`
        )
      ]);
  
      setPartySpecificOptions({
        pStyles: pStylesRes.data.message || [],
        styles: stylesRes.data.message || [],
        items: itemsRes.data.message || []
      });
    } catch (error) {
      console.error('Error fetching party-specific options:', error);
      toast.error('Error fetching options for selected party');
    }
  };
  
  const handlePartyChange = async (value) => {
    setNewRate(prev => ({
      ...prev,
      party: value,
      p_style: '',
      style: '',
      item: ''
    }));

    setAddRecordDialog(prev => ({ ...prev, party: value }));
    if (value) {
      await fetchPartySpecificOptions(value);
    } else {
      setPartySpecificOptions({
        pStyles: [],
        styles: [],
        items: []
      });
    }
  };

  const handleOpenAddRecordDialog = (type) => {
    setAddRecordDialog({
      open: true,
      type,
      name: '',
      party: '',
      customer: ''
    });
  };

  const handleCloseAddRecordDialog = () => {
    setAddRecordDialog({
      open: false,
      type: '',
      name: '',
      party: '',
      customer: ''
    });
  };

  const handleAddRecord = async () => {
    if (!addRecordDialog.name.trim()) {
      toast.warn('Name cannot be empty.');
      return;
    }

    if (addRecordDialog.type === 'party' && !addRecordDialog.customer) {
      toast.warn('Please select a customer.');
      return;
    }

    if(newRate?.party){
      if (['style', 'pStyle', 'item'].includes(addRecordDialog.type) && !newRate.party) {
        toast.warn('Please select a party.');
        return;
      }
    }else{
      if (['style', 'pStyle', 'item'].includes(addRecordDialog.type) && !addRecordDialog.party) {
        toast.warn('Please select a party.');
        return;
      }
    }

    try {
      const endpoints = {
        party: 'inventory/party',
        pStyle: 'inventory/p-style',
        style: 'inventory/style',
        item: 'inventory/item'
      };

      let payload;
      if (addRecordDialog.type === 'party') {
        payload = {
          name: addRecordDialog.name,
          customer: addRecordDialog.customer
        };
      } else if (['style', 'pStyle', 'item'].includes(addRecordDialog.type)) {
        payload = {
          name: addRecordDialog.name,
          party: addRecordDialog.party?addRecordDialog.party:newRate.party
        };
      } else {
        payload = {
          name: addRecordDialog.name
        };
      }

      const response = await api.post(endpoints[addRecordDialog.type], payload);

      if (response.status === 200 || response.OK) {
        toast.success('Added successfully');
        // Refresh the dropdown data and party-specific options if needed
        await fetchDropdownData();
        if (newRate.party) {
          await fetchPartySpecificOptions(newRate.party);
        }
        handleCloseAddRecordDialog();
      }
    } catch (error) {
      toast.error('Failed to add record. Please try again.');
      console.error('Error adding record:', error);
    }
  };

  const handleDeleteClick = (rate) => {
    setDeleteConfirmDialog({
      open: true,
      rateId: rate._id,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`inventory/rate/${deleteConfirmDialog.rateId}`);
      toast.success('Rate deleted successfully');
      fetchRates(pprRef.current,filtersRef.current);
    } catch (error) {
      toast.error('Error deleting rate');
      console.error('Error deleting rate:', error);
    } finally {
      setDeleteConfirmDialog({ open: false, rateId: null });
    }
  };
  const handlePStyleChange = (pStyleId) => {
    setNewRate({ ...newRate, p_style: pStyleId, item: '' });
    fetchPartySpecificOptions(newRate.party, pStyleId);
  };
  
  const dialogContent = (
    <DialogContent>
      <Box display="flex" flexDirection="column" gap={2}>
        <CustomSelect
          fullWidth
          label="Customer"
          value={newRate.customer}
          onChange={(e) => setNewRate({ ...newRate, customer: e.target.value })}
          options={customers.map((c) => ({ id: c._id, name: c.name }))}
          sx={{ minWidth: 150 }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <CustomSelect
            fullWidth
            label="Party"
            value={newRate.party}
            onChange={(e) => handlePartyChange(e.target.value)}
            options={parties.map((c) => ({ id: c._id, name: c.name }))}
            sx={{ minWidth: '84%' }}
          />
          <IconButton
            onClick={() => handleOpenAddRecordDialog('party')}
            sx={{
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 }
            }}
          >
            <AddIcon sx={{ color: 'black' }} />
          </IconButton>
          {/* Added new Thing */}
          <IconButton
            onClick={() => handleOpenManagementDialog('party')}
            sx={{

              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            <Tooltip title="Management">
              <SettingsIcon sx={{ color: 'black', fontSize: 22 }} />
            </Tooltip>
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* <CustomSelect
            fullWidth
            label="P Style/Style"
            value={newRate.p_style}
            onChange={(e) => setNewRate({ ...newRate, p_style: e.target.value })}
            options={partySpecificOptions.pStyles.map((c) => ({ id: c._id, name: c.name }))}
            sx={{ minWidth: '84%' }}
            disabled={!newRate.party}
          /> */}
          <CustomSelect
  fullWidth
  label="P Style/Style"
  value={newRate.p_style}
  onChange={(e) => handlePStyleChange(e.target.value)}
  options={partySpecificOptions.pStyles.map((c) => ({ id: c._id, name: c.name }))}
  sx={{ minWidth: '84%' }}
  disabled={!newRate.party}
/>
          <IconButton
            onClick={() => handleOpenAddRecordDialog('pStyle')}
            // disabled={!newRate.party}
            sx={{
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            <AddIcon sx={{ color: 'black' }} />
          </IconButton>
          <IconButton
            onClick={() => handleOpenManagementDialog('pStyle')}
            sx={{

              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            <Tooltip title="Management">
              <SettingsIcon sx={{ color: 'black', fontSize: 22 }} />
            </Tooltip>
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <CustomSelect
            fullWidth
            label="Item"
            value={newRate.item}
            onChange={(e) => setNewRate({ ...newRate, item: e.target.value })}
            options={partySpecificOptions.items.map((c) => ({ id: c._id, name: c.name }))}
            sx={{ minWidth: '84%' }}
            disabled={!newRate.party}
          />
          <IconButton
            onClick={() => handleOpenAddRecordDialog('item')}
            // disabled={!newRate.party}
            sx={{
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            <AddIcon sx={{ color: 'black' }} />
          </IconButton>
          <IconButton
            onClick={() => handleOpenManagementDialog('item')}
            sx={{

              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            <Tooltip title="Management">
              <SettingsIcon sx={{ color: 'black', fontSize: 22 }} />
            </Tooltip>
          </IconButton>
        </Box>
        <TextField
          fullWidth
          margin="dense"
          label="Price"
          type="number"
          value={newRate.price}
          onChange={(e) => setNewRate({ ...newRate, price: e.target.value })}
        />
      </Box>
    </DialogContent>
  );

  const addRecordDialogComponent = (
    <Dialog open={addRecordDialog.open} onClose={handleCloseAddRecordDialog}>
      <DialogTitle>Add New {addRecordDialog.type}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Show customer select only for party */}
          {addRecordDialog.type === 'party' && (
            <CustomSelect
              label="Customer"
              value={addRecordDialog.customer}
              onChange={(e) => setAddRecordDialog(prev => ({ ...prev, customer: e.target.value }))}
              options={customers.map(customer => ({
                id: customer._id,
                name: customer.name
              }))}
              fullWidth
            />
          )}
          {/* Show party select only for style, pStyle, and item */}
          {['style', 'pStyle', 'item'].includes(addRecordDialog.type) && (
            <CustomSelect
              label="Party"
              value={newRate.party || addRecordDialog.party}
              onChange={(e) => setAddRecordDialog(prev => ({ ...prev, party: e.target.value }))}
              options={parties.map(party => ({
                id: party._id,
                name: party.name
              }))}
              fullWidth
            />
          )}
          <TextField
            autoFocus
            label="Name"
            type="text"
            fullWidth
            value={addRecordDialog.name}
            onChange={(e) => setAddRecordDialog(prev => ({ ...prev, name: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAddRecordDialog}>Cancel</Button>
        <Button
          onClick={handleAddRecord}
          sx={{
            backgroundColor: colors.primary,
            color: 'black',
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9
            }
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
  const pprRef=useRef(rowsPerPage);
  const filtersRef=useRef(filters);
  useEffect(() => {
    pprRef.current=rowsPerPage;
  }, [rowsPerPage]);
  useEffect(() => {
    filtersRef.current=filters;
  }, [filters]);
  useEffect(() => {
    fetchDropdownData();
  }, []);
  useEffect(() => {
    if (filters.customer || filters.customer === 'all') fetchRates(pprRef.current,filtersRef.current);
    
  }, [filters, page, rowsPerPage]);
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRates(pprRef.current,filtersRef.current)
    }, 10000); 
    return () => clearInterval(interval);
  }, []);
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: 1, sm: 2, md: 3 }, // Responsive padding
        width: '95%', // Take full available width
        maxWidth: '1600px', // Limit content width on very large screens
        margin: '0 auto', // Center the content
      }}
    >
      <Navbar title="Rates" />
      {/* Rate History Dialog (assuming RateBox is a dialog) */}
      <RateBox dialogOpen={historyDialog} selectedRate={selectedRate} handleCloseDialog={handleCloseHistoryDialog} loading={loading} />

      {/* Filters and Add Rate Button Section */}
      <Box
        sx={{
          marginTop: { xs: 8, sm: 10 }, // Adjust top margin for fixed Navbar
          marginBottom: { xs: 2, sm: 3 }, // Responsive bottom margin
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Stack on small, row on medium/large
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' }, // Align items (top for stacked, center for row)
          gap: { xs: 2, sm: 3 }, // Responsive gap between filter group and button
        }}
      >
        {/* Filters Box */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap', // Allow filters to wrap
            gap: { xs: 1, sm: 1.5, md: 2 }, // Responsive gap between individual filters
            width: { xs: '100%', md: 'auto' }, // Full width on small, auto on medium/large
            justifyContent: { xs: 'center', sm: 'flex-start' }, // Center on xs, left-align on sm+
            flexGrow: 1, // Allow filters to grow and take available space
            maxWidth: { xs: '100%', md: 'calc(100% - 200px)' }, // Limit width on larger screens to make space for button
          }}
        >
          <CustomSelect
            label="Filter by Customer"
            value={filters.customer}
            onChange={(e) => handleFilterChange('customer', e.target.value)}
            options={[
              { id: 'all', name: 'All Customers' },
              ...customers.map((c) => ({ id: c._id, name: c.name }))
            ]}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 150, md: 160 }, // Two columns on xs, wider on sm/md+
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"} // Smaller input on small screens
          />
          <CustomSelect
            label="Filter by Party"
            value={filters.party}
            onChange={(e) => handleFilterChange('party', e.target.value)}
            options={[
              { id: 'all', name: 'All Parties' },
              ...parties.map((p) => ({ id: p._id, name: p.name }))
            ]}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 150, md: 160 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"}
          />
          <CustomSelect
            label="Filter by P Style/Style"
            value={filters.p_style}
            onChange={(e) => handleFilterChange('p_style', e.target.value)}
            options={[
              { id: 'all', name: 'All P Styles' },
              ...pStyles.map((p) => ({ id: p._id, name: p.name }))
            ]}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 150, md: 160 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"}
          />
          {/* <CustomSelect
            label="Filter by Style"
            value={filters.style}
            onChange={(e) => handleFilterChange('style', e.target.value)}
            options={[
              { id: 'all', name: 'All Styles' },
              ...styles.map((s) => ({ id: s._id, name: s.name }))
            ]}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 150, md: 160 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"}
          /> */}
          <CustomSelect
            label="Filter by Item"
            value={filters.item}
            onChange={(e) => handleFilterChange('item', e.target.value)}
            options={[
              { id: 'all', name: 'All Items' },
              ...items.map((i) => ({ id: i._id, name: i.name }))
            ]}
            sx={{
              minWidth: { xs: 'calc(50% - 8px)', sm: 150, md: 160 },
              flexGrow: 1,
            }}
            size={isSmallScreen ? "small" : "medium"}
          />
        </Box>

        {/* Add Rate Button */}
        {(userInfo?.role === 'admin' || accessControl["add-rates"] === true) &&
          <Button
            sx={{
              backgroundColor: colors.primary,
              color: 'black',
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
              width: { xs: '100%', md: 'auto' }, // Full width on small, auto on medium/large
              mt: { xs: 2, md: 0 }, // Top margin on small, no top margin on medium/large
              minHeight: { xs: '48px', sm: '56px' }, // Ensure button is tappable
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Rate
          </Button>}
      </Box>

      {/* Rates Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}> {/* Enable horizontal scroll */}
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Customer</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Party</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>P Style/Style</TableCell>
              {/* <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Style</TableCell> */}
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Item</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Price</TableCell>
              <TableCell sx={{ backgroundColor: colors.primary, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates?.map((rate) => (
              <TableRow key={rate?._id}>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.customer?.name}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.party?.name}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.p_style?.name}</TableCell>
                {/* <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.style?.name}</TableCell> */}
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.item?.name}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{rate?.price}</TableCell>
                <TableCell sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' }, // Stack buttons on xs, row on sm+
                  gap: { xs: 0.5, sm: 1 }, // Smaller gap when stacked
                  alignItems: { xs: 'flex-start', sm: 'center' }, // Align top on xs, center on sm+
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenHistoryDialog(rate)}
                    sx={{
                      backgroundColor: colors.primary, borderColor: colors.primary,
                      '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      minWidth: { xs: 'auto', sm: 60 }, // Adjust minWidth for smaller screens
                      padding: { xs: '4px 8px', sm: '6px 12px' }
                    }}
                  >
                    View
                  </Button>
                  {(userInfo?.role === 'admin' || accessControl["manage-rates"] === true) &&
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdateDialogOpen(rate)}
                        sx={{
                          backgroundColor: colors.primary, borderColor: colors.primary,
                          '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          minWidth: { xs: 'auto', sm: 60 },
                          padding: { xs: '4px 8px', sm: '6px 12px' }
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleDeleteClick(rate)}
                        sx={{
                          backgroundColor: colors.primary, borderColor: colors.primary,
                          '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          minWidth: { xs: 'auto', sm: 60 },
                          padding: { xs: '4px 8px', sm: '6px 12px' }
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination and Rows per page */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Stack on xs, row on sm+
          justifyContent: { xs: 'center', sm: 'space-between' }, // Center on xs, space-between on sm+
          alignItems: 'center',
          mt: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 2, sm: 0 } // Gap when stacked
        }}
      >
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          size={isSmallScreen ? "small" : "medium"} // Smaller pagination on small screens
        />
        <CustomSelect
          label="Rows per page"
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          options={[
            // { id: 5, name: '5' },
            // { id: 10, name: '10' },
            // { id: 25, name: '25' },
            // { id: 50, name: '50' },
            { id: 100, name: '100' },
            { id: 200, name: '200' },
            { id: 300, name: '300' },
            { id: 400, name: '400' },
            { id: 500, name: '500' },
          ]}
          sx={{ minWidth: { xs: '100%', sm: 120 } }} // Full width on xs, fixed on sm+
          size={isSmallScreen ? "small" : "medium"}
        />
      </Box>

      {/* Add/Edit Rate Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm"
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 }, // Responsive margin
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }, // Responsive width
            padding: { xs: '10px', sm: '16px' }, // Responsive padding
            minHeight: { xs: '70vh', sm: 'auto' }, // Make dialog taller on small screens
            maxHeight: '90vh', // Prevent dialog from being too tall
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: colors.primary,
            fontSize: { xs: '1.2rem', sm: '1.4rem' },
            pb: 0.5,
            pr: { xs: 5, sm: 8 } // Add right padding to prevent close button overlap
          }}
        >
          {isEditing ? 'Edit Rate' : 'Add Rate'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 16 }, // Adjust right position for close button
              top: { xs: 8, sm: 16 }, // Adjust top position for close button
              color: 'black'
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        {dialogContent}
        <DialogActions
          sx={{
            display: 'flex',
            // justifyContent: 'space-between',
            padding: { xs: 1, sm: '16px' },
            flexDirection: { xs: 'column', sm: 'row' }, // Stack on xs, row on sm+
            gap: { xs: 1, sm: 0 } // Gap when stacked
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'black',
              paddingLeft: { xs: 2, sm: 20 },
              paddingRight: { xs: 2, sm: 20 },
              width: { xs: '100%', sm: 'auto' }, // Full width on xs
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRate}
            sx={{
              backgroundColor: colors.primary,
              color: 'white',
              paddingLeft: { xs: 2, sm: 20 },
              paddingRight: { xs: 2, sm: 20 },
              width: { xs: '100%', sm: 'auto' }, // Full width on xs
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 }
            }}
          >
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {addRecordDialogComponent} {/* Ensure this component is also responsive */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, rateId: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            padding: { xs: '10px', sm: '16px' },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
          Confirm Delete
          <IconButton
            onClick={() => setDeleteConfirmDialog({ open: false, rateId: null })}
            sx={{
              position: 'absolute', right: { xs: 8, sm: 16 }, top: { xs: 8, sm: 16 },
              color: 'black'
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          Are you sure you want to delete this rate?
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1, sm: 2 }, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, rateId: null })}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: colors.primary,
              color: 'black',
              '&:hover': { backgroundColor: colors.primary, opacity: 0.9 },
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ManagementDialog
        open={managementDialogOpen}
        onClose={() => setManagementDialogOpen(false)}
        type={managementType}
        partyId={newRate?.party}
        pstyle={newRate?.p_style}

      />
    </Box>
  );
};

export default Rates;
